import { DependencyList, useCallback } from 'react'
import {
	ApiGetStreamWithToken,
  ApiGetWithToken,
	ApiPostWithToken,
	useApiFetch,
	useApiPost
} from 'shared/api'
import {
	searchsSisetuApi,
	searchsSisetuApiBy,
	searchsTrialApiBy,
	sisetuApi,
	sisetuApiBy
} from 'interface/endpoints'
import {
	SisetuResult,
	SisetuResultList,
	SisetuSummaryFormData,
	SisetuSummaryResult,
} from './types'
import { useApiFetchStream } from 'shared/api/apiFetchStream'
import { useUpdateTime } from 'interface/util'

export * from './types'

type useListSisetuParams = {
	prefectureCodes?: string[],
	searchWord?: string | null,
}
export const useListSisetu = (
	searchId: number | undefined | null,
	{ prefectureCodes, searchWord }: useListSisetuParams = {},
	option: {
		updateTime?: string
		rankLimit?: number
		onPreFetch?: () => void
		onFetched?: (result: SisetuResult[]) => void
	} = {},
	deps?: DependencyList
) => {
	const { updateTime, rankLimit, onPreFetch, onFetched } = option
	const t = useUpdateTime(updateTime)
	return useApiFetchStream<SisetuResult>(
		useCallback(
			async (apiAccess, abort, onInitialize, onLoad) => {
				const result: SisetuResult[] = []
				await ApiGetStreamWithToken<SisetuResult>(
					apiAccess,
					searchId ? searchsSisetuApi(searchId, '/stream', {
						t,
						pref: prefectureCodes?.join(','),
						word: searchWord,
						rankLimit
					}) : sisetuApi('/stream', {
						t,
						pref: prefectureCodes?.join(','),
						word: searchWord
					}),
					(count) => {
						if (onPreFetch) onPreFetch()
						onInitialize(count)
					},
					async (kojin) => {
						result.push(kojin)
						onLoad(result.length)
					},
					abort?.signal ?? null
				)
				if (onFetched) onFetched(result)
				return result
			},
			[t, searchId, prefectureCodes, searchWord, rankLimit, onPreFetch, onFetched]
		),
		deps
	)
}

export const useGetSisetu = (
	searchId: number | null | undefined,
	sisetuCode: string | null | undefined,
	option: {
		defaultSisetu?: SisetuResult
		updateTime?: string
	} = {}
) => {
	const { defaultSisetu, updateTime } = option
	const t = useUpdateTime(updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!sisetuCode) return null
				if (defaultSisetu && defaultSisetu.sisetuCode === sisetuCode) return defaultSisetu
				return ApiGetWithToken<SisetuResult>(
					apiAccess,
					searchId ? searchsSisetuApiBy(searchId, sisetuCode) :
					sisetuApiBy(sisetuCode, undefined, { t }),
					cancelToken
				)
			},
			[t, searchId, sisetuCode, defaultSisetu]
		)
	)
}

export const useListSisetuByTrial = (
	searchId: number | null | undefined,
	trialId: number | null | undefined,
	option: {
		updateTime?: string
	} = {}
) => {
	const t = useUpdateTime(option.updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId || !trialId) return null
				return ApiGetWithToken<SisetuResultList>(
					apiAccess,
					searchsTrialApiBy(searchId, trialId, '/sisetu/list', { t }),
					cancelToken
				)
			},
			[t, searchId, trialId]
		)
	)
}

export const useListChildrenSisetu = (
	sisetuCode: string | undefined | null,
	option: {
		updateTime?: string
	} = {}
) => {
	const t = useUpdateTime(option.updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!sisetuCode) return null
				return ApiGetWithToken<SisetuResultList>(
					apiAccess,
					sisetuApiBy(sisetuCode, '/children', { t }),
					cancelToken
				)
			},
			[t, sisetuCode]
		)
	)
}

export const useGetSisetuSummary = (
	searchId: number | null | undefined,
	sisetuCode: string | null | undefined,
	option: {
		updateTime?: string
	} = {}
) => {
	const t = useUpdateTime(option.updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId || !sisetuCode) return null
				return ApiGetWithToken<SisetuSummaryResult>(
					apiAccess,
					searchsSisetuApiBy(searchId, sisetuCode, '/summary', { t }),
					cancelToken
				)
			},
			[t, searchId, sisetuCode]
		)
	)
}
export const usePostSisetuSummary = (
	searchId: number | undefined | null,
	sisetuCode: string,
	callback?: {
		onPosted?: (result: SisetuSummaryResult) => void
		onError?: (reason: Error | undefined) => void
	}
) => useApiPost<SisetuSummaryFormData, SisetuSummaryResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			if (!searchId) return null
			return ApiPostWithToken(
				apiAccess,
				searchsSisetuApiBy(searchId, sisetuCode, '/summary'),
				form,
				cancelToken,
				webSocketId
			)
		},
		[searchId, sisetuCode]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
			if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const usePostSisetuSummaryGenerativeAI = (
	searchId: number | null | undefined,
	sisetuCode: string | null | undefined,
	callback?: {
		onPosted?: (result: string | null) => void
		onError?: (reason: Error | undefined) => void
	}
) => useApiPost<void, string | null>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			if (!searchId || !sisetuCode) return null
			return ApiPostWithToken(
				apiAccess,
				searchsSisetuApiBy(searchId, sisetuCode, '/summary/genAI'),
				form,
				cancelToken,
				webSocketId
			)
		},
		[searchId, sisetuCode]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
			if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)
